import { CpuChipIcon } from '@heroicons/react/24/outline';

import { Lock, LockHook, Station } from '../../../services/models/assets';
import { Button } from '@follow/ui';

interface StationArchitectureDiagramProps {
  station: Station;
  // Access-code generation belongs to the rentals feature; without it the lock
  // row keeps only its open/activate controls.
  codesEnabled: boolean;
  onOpenLock: (lock: Lock) => void;
  onGenerateCode: (lock: Lock) => void;
  onToggleLock: (lock: Lock) => void;
}

const StationIcon: React.FC = () => (
  <svg
    width="154"
    height="158"
    viewBox="0 0 154 158"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: 32, height: 32, filter: 'brightness(0)', flexShrink: 0 }}
  >
    <defs>
      <filter
        id="_ecu_shadow"
        x="0"
        y="0"
        width="153.458"
        height="157.123"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="1.007" />
        <feGaussianBlur stdDeviation="3.021" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
      </filter>
    </defs>
    <g filter="url(#_ecu_shadow)">
      <path
        d="M129.744 56.84H76.615C73.363 56.84 70.36 59.451 69.907 62.671L64.998 97.601C64.545 100.822 66.815 103.432 70.067 103.432H112.228C121.413 103.432 129.914 96.108 131.284 87.013L132.297 80.296C132.644 77.988 131.027 76.106 128.697 76.106H117.266C115.828 76.106 114.521 77.308 114.413 78.729L114.334 79.773C114.227 81.194 112.919 82.396 111.481 82.396H104.425C103.194 82.396 102.273 81.505 102.258 80.3L102.18 73.963C102.162 72.471 103.517 71.128 105.04 71.128H129.437C131.751 71.128 133.888 69.27 134.209 66.979L134.814 62.669C135.265 59.45 132.996 56.84 129.744 56.84Z"
        fill="#1A1A1A66"
      />
      <path
        d="M17.624 62.45C18.059 59.352 20.948 56.84 24.077 56.84H60.157C63.286 56.84 65.469 59.352 65.034 62.45L54.962 134.117C53.723 142.929 45.505 150.073 36.606 150.073H10.974C7.845 150.073 5.662 147.562 6.098 144.464L17.624 62.45Z"
        fill="#1A1A1A66"
      />
      <path
        d="M23.6 20.991C24.839 12.179 33.057 5.035 41.956 5.035H142.484C145.612 5.035 147.796 7.547 147.36 10.645L143.843 35.672C142.604 44.484 134.386 51.628 125.487 51.628H24.959C21.831 51.628 19.648 49.117 20.083 46.019L23.6 20.991Z"
        fill="#1A1A1A66"
      />
    </g>
  </svg>
);

const ClosedLockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#2D7A4F">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const OpenedLockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#E06B2E">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const AddIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 3v8M3 7h8" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const EcuSection: React.FC<{
  label: string;
  locks: Lock[];
  maxLocks: number;
  codesEnabled: boolean;
  onOpenLock: (lock: Lock) => void;
  onGenerateCode: (lock: Lock) => void;
  onToggleLock: (lock: Lock) => void;
}> = ({ label, locks, maxLocks, codesEnabled, onOpenLock, onGenerateCode, onToggleLock }) => {
  const emptySlots = Math.max(0, maxLocks - locks.length);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center rounded-2xl flex-col gap-2 border border-dashed border-[#1A1A1A33] shrink-0 size-16">
          <StationIcon />
        </div>
        <div className="flex flex-col">
          <div className="typo-small text-neutral !font-bold">{label}</div>
          <div className="typo-caption text-neutral-60">
            {locks.length} lock{locks.length > 1 ? 's' : ''} connecté
            {locks.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {locks.map((lock) => {
          const isClosed = lock.hook === LockHook.CLOSED;
          const isOpened = lock.hook === LockHook.OPENED;
          return (
            <div
              key={lock.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-solid border-[#1A1A1A1A]"
            >
              <div
                className="w-[22px] h-[22px] flex items-center justify-center rounded-md shrink-0"
                style={{
                  background: isClosed
                    ? '#E8F5EE'
                    : isOpened
                    ? '#FFF0E8'
                    : '#1A1A1A0D',
                }}
              >
                {isOpened ? <OpenedLockIcon /> : <ClosedLockIcon />}
              </div>
              <p className="text-neutral typo-body !font-semibold flex-1">
                Lock {lock.position + 1}
              </p>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="primary"
                  tone="light"
                  size="sm"
                  disabled={!lock.active}
                  onClick={() => onOpenLock(lock)}
                >
                  Ouvrir
                </Button>
                {codesEnabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onGenerateCode(lock)}
                    disabled={!lock.active}
                  >
                    Générer un accès
                  </Button>
                )}
                {!lock.active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400"
                    onClick={() => onToggleLock(lock)}
                  >
                    Activer
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {Array.from({ length: emptySlots }, (_, i) => (
          <div
            key={`empty-${locks.length + i}`}
            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-solid border-[#1A1A1A1A] opacity-50"
          >
            <div className="w-[22px] h-[22px] flex items-center justify-center rounded-md bg-[#1A1A1A0D] shrink-0">
              <AddIcon />
            </div>
            <p className="text-neutral typo-body !font-semibold flex-1">
              Slot {locks.length + i + 1}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const StationArchitectureDiagram: React.FC<StationArchitectureDiagramProps> = ({
  station,
  codesEnabled,
  onOpenLock,
  onGenerateCode,
  onToggleLock,
}) => {
  const sortedEcus = (station.ecus || []).sort((a, b) => a.address - b.address);
  const totalLocks = sortedEcus.reduce((sum, ecu) => sum + (ecu.locks?.length ?? 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10">
      <div className="flex items-center justify-between p-4 border-b border-neutral-10">
        <div className="flex items-center gap-2">
          <CpuChipIcon className="w-5 h-5 text-primary-500" />
          <h3 className="text-neutral typo-h3">Architecture de la station</h3>
        </div>
        <span className="typo-small">
          <span className="text-neutral">{sortedEcus.length}</span>
          <span className="text-neutral-60"> Module{sortedEcus.length > 1 ? 's' : ''} · </span>
          <span className="text-neutral">{totalLocks}</span>
          <span className="text-neutral-60"> lock{totalLocks > 1 ? 's' : ''}</span>
        </span>
      </div>

      <div className="p-4 bg-off-white rounded-b-xl flex flex-col gap-4">
        {sortedEcus.map((ecu) => (
          <EcuSection
            key={ecu.id}
            label={ecu.isPrincipal ? 'Module principal' : `Module #${ecu.address}`}
            locks={[...(ecu.locks || [])].sort((a, b) => a.position - b.position)}
            maxLocks={ecu.maxLocks}
            codesEnabled={codesEnabled}
            onOpenLock={onOpenLock}
            onGenerateCode={onGenerateCode}
            onToggleLock={onToggleLock}
          />
        ))}
      </div>
    </div>
  );
};

export default StationArchitectureDiagram;
